<template>
  <div>
    <login-page @successLogin="successLogin" />
    <transition name="fade">
      <!--
        It is only for non authorized users.
        Transition required to avoid blicks when reload page with active session
        It appears cause /account/metadata resolved first, and profile has'nt resolved yet
        we have isLoggedIn equal false and isSuspended true while profile still pending,
        and after it resolve it just remove suspeded-account block, and we have blick
      -->
      <suspended-account v-if="isSuspended && !isLoggedIn" />
    </transition>
    <qx-keep-alive />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
// Components
import LoginPage from '@/components/auth/LoginPage.vue';

// Elements
import EventBus from '@/elements/eventBus.js';
import QxKeepAlive from '@/elements/keepAlive.vue';

// Mixins
import handleErrorMixin from '@/mixins/handleErrorMixin.js';

import { checkError } from '@/utils/mainCheckPermissions.js';

export default {
  name: 'QxAuthorization',
  components: {
    LoginPage,
    QxKeepAlive,
    SuspendedAccount: defineAsyncComponent(() => import('@/elements/SuspendedAccount.vue')),
  },
  mixins: [handleErrorMixin],
  emits: ['pageReady', 'successLogin'],
  computed: {
    ...mapState({
      isLoggedIn: (state) => state.auth.isLoggedIn,
      delayedCall: (state) => state.delayedCall,
      profileId: (state) => state.profile.id,
      isStandAlonePage: (state) => state.isStandAlonePage,
    }),
    ...mapGetters({
      isSuspended: 'isSuspended',
    }),
  },
  watch: {
    isLoggedIn(newValue) {
      // We call checkPermissions to pull metadata on standalone pages, when loged in/loged out on other tab
      if (!newValue) {
        // clean all notifications before to not show them on login page
        this.$notify({
          clean: true,
        });
        this.resetStore();
        if (this.isStandAlonePage) {
          this.checkPermissions();
        }
      }
      if (newValue && this.isStandAlonePage) {
        this.checkPermissions();
      }
    },
    async profileId(newValue, oldValue) {
      // When userId changed from another tab, we need refresh user info
      if (oldValue && newValue) {
        await this.getProfile();
      }
    },
    '$route.params.id': {
      handler(val) {
        if (val || !this.isStandAlonePage) {
          this.checkApp();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.defineGlobalResponces();
    EventBus.$on('changePage', this.checkApp);
  },
  methods: {
    ...mapMutations({
      setErrorPageKey: 'setErrorPageKey',
      setIsForcedLogin: 'auth/setIsForcedLogin',
      clearDelayedCall: 'clearDelayedCall',
      setLoginError: 'auth/setLoginError',
      setMfaCode: 'auth/setMfaCode',
    }),
    ...mapActions({
      resetStore: 'resetStore',
      getAccountMetadata: 'getAccountMetadata',
      getProfile: 'profile/getProfile',
    }),
    checkError,
    defineGlobalResponces() {
      API.onApiError = (args, xhr, resp, localError) => {
        let noGlobalError = false;
        let options = {};
        if (typeof localError === 'function') {
          const localResponse = localError();
          if (typeof localResponse === 'object') {
            options = localResponse;
          } else {
            noGlobalError = localResponse;
          }
        }
        if (!noGlobalError) {
          const globalResponse = this.checkError({ status: xhr.status, resp, options: { args } });
          if (!globalResponse) {
            if (typeof options.onErrorCallback === 'function') {
              options.onErrorCallback();
            } else {
              this.$notify({
                text: options.errorMsg || resp.msg,
                type: 'error',
              });
            }
          }
        }
      };
    },
    async checkApp() {
      try {
        await this.getAccountMetadata();
        if (this.isStandAlonePage && this.isSuspended) {
          this.setErrorPageKey('errorSuspended');
          return;
        }
        this.checkPermissions();
      } catch {}
    },
    async checkPermissions() {
      try {
        await this.currentPermissionRules.checkPermissions();
        this.checkDelayedCall();
        this.setIsForcedLogin(false);
      } catch (error) {
        if (error?.response) {
          this.handleErrorFromRequest(error);
        }
      } finally {
        this.$emit('pageReady');
      }
    },
    checkDelayedCall() {
      if (this.delayedCall) {
        API._request(this.delayedCall);
        this.clearDelayedCall();
      }
    },
    async successLogin() {
      this.setMfaCode(null);
      this.$emit('successLogin');
      this.checkPermissions();
    },
  },
};
</script>
